import apiService from './api';

/** New permission catalog/expansion endpoints (2026-05-16). */
export interface ExpansionResult {
  picks: string[];
  expanded: string[];
  autoIncluded: string[];
  breakdown: { permission: string; pulledInBy: string[] }[];
}

// Backend wraps every response in { status: 'success', data: {...} }; apiService
// passes the body through unwrapped at the top level only, so for these endpoints
// we have to peel one more layer manually.
type ApiEnvelope<T> = { status: string; data: T } | T;
const unwrap = <T>(res: ApiEnvelope<T>): T =>
  (res && typeof res === 'object' && 'data' in (res as any)
    ? ((res as { data: T }).data as T)
    : (res as T));

class PermissionExpansionAPI {
  private base = '/admin/permissions';

  /** Compute the full expansion + breakdown for a set of picks. */
  public async expand(picks: string[]): Promise<ExpansionResult> {
    const res = await apiService.post<ApiEnvelope<ExpansionResult>>(`${this.base}/expand`, { picks });
    return unwrap(res);
  }

  /** Inverse: derive minimal picks from a legacy permissions[] set. */
  public async inverseExpand(permissions: string[]): Promise<{ picks: string[]; recovered: string[] }> {
    const res = await apiService.post<ApiEnvelope<{ picks: string[]; recovered: string[] }>>(
      `${this.base}/inverse-expand`,
      { permissions },
    );
    return unwrap(res);
  }

  /** Direct + transitive deps for a single permission code. */
  public async getDependencies(code: string): Promise<{ code: string; direct: string[]; transitive: string[] }> {
    const res = await apiService.get<ApiEnvelope<{ code: string; direct: string[]; transitive: string[] }>>(
      `${this.base}/${encodeURIComponent(code)}/dependencies`,
    );
    return unwrap(res);
  }
}

export const permissionExpansionAPI = new PermissionExpansionAPI();

export interface Permission {
  id: string;
  name: string;
  description: string;
  code: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

export interface RoutePermission {
  id: string;
  name: string;
  description?: string;
  parentId?: string;
  order?: number;
  children?: RoutePermission[];
  path?: string;
  method?: string;
}

export interface CreatePermissionDto {
  name: string;
  description: string;
  code: string;
  category: string;
}

export interface UpdatePermissionDto {
  name?: string;
  description?: string;
  code?: string;
  category?: string;
}

export interface PermissionCategory {
  name: string;
  permissions: Permission[];
}

class PermissionService {
  private baseUrl = '/admin/permissions';
  private routesUrl = '/admin/routes';

  // Get all permissions with pagination
  public async getPermissions(page: number = 0, limit: number = 10, search?: string) {
    return apiService.getPaginated<Permission>(this.baseUrl, { page, limit, search });
  }

  /**
   * Get all permissions — sourced from the bundled `PERMISSION_CATALOG`.
   * The old `/admin/routes/*` endpoint was deleted in the Session 3 cutover
   * (the dynamic route registry is gone); the frontend catalog mirror is
   * regenerated by `sync-frontend-catalog.ts` and is the canonical source.
   */
  public async getAllPermissions(): Promise<Permission[]> {
    // Lazy import to avoid bundling the catalog twice in callers that only
    // use grant endpoints.
    const { PERMISSION_CATALOG } = await import('../constants/permissionCatalog');
    const now = new Date().toISOString();
    return Object.entries(PERMISSION_CATALOG).map(([code, meta]) => ({
      id: code,
      code,
      name: (meta as any).nameFa ?? code,
      description: (meta as any).description ?? (meta as any).nameFa ?? code,
      category: (meta as any).domain ?? 'Other',
      createdAt: now,
      updatedAt: now,
    }));
  }

  /**
   * Deprecated — `/admin/routes/*` endpoint was deleted in Session 3.
   * Kept as a thin wrapper around `getAllPermissions` so any straggler caller
   * still gets data (in a different shape — codes wrapped as RoutePermissions).
   */
  public async getAllRoutes(): Promise<RoutePermission[]> {
    const perms = await this.getAllPermissions();
    return perms.map((p) => ({ id: p.code, name: p.name, description: p.description }));
  }

  // Flatten the route tree to a simple array of permissions
  private flattenRoutes(routes: RoutePermission[]): Permission[] {
    const permissions: Permission[] = [];
    
    const processRoute = (route: RoutePermission, parentName: string = '') => {
      // دسته‌بندی را براساس اولین بخش ID یا از parent گروه‌بندی می‌کنیم
      let category = '';
      
      if (route.id.includes('_')) {
        // استخراج بخش اول ID به عنوان دسته (مثال: admin_users -> ADMIN)
        category = route.id.split('_')[0].toUpperCase();
      } else if (route.name && route.name.includes(' ')) {
        // اگر نام شامل فاصله باشد، از کلمه اول استفاده می‌کنیم
        category = route.name.split(' ')[0].toUpperCase();
      } else {
        // اگر هیچ کدام نباشد، از خود ID استفاده می‌کنیم
        category = route.id.toUpperCase();
      }
      
      // Add this route as a permission
      permissions.push({
        id: route.id,
        name: route.name || route.id,
        description: route.description || route.name || route.id,
        code: route.id,
        category: category,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      
      // Process children recursively
      if (route.children && route.children.length > 0) {
        route.children.forEach(child => processRoute(child, route.name || ''));
      }
    };
    
    routes.forEach(route => processRoute(route));
    return permissions;
  }

  // Get permissions grouped by category
  public async getPermissionsByCategory() {
    try {
      const permissions = await this.getAllPermissions();
      
      // گروه‌بندی بر اساس دسته
      const grouped = permissions.reduce((acc, permission) => {
        if (!acc[permission.category]) {
          acc[permission.category] = [];
        }
        acc[permission.category].push(permission);
        return acc;
      }, {} as Record<string, Permission[]>);
      
      // تبدیل به فرمت مورد نیاز
      return Object.keys(grouped).map(category => ({
        name: category,
        permissions: grouped[category]
      }));
    } catch (err) {
      console.error('Error getting permissions by category:', err);
      return [];
    }
  }

  // Get permission by ID
  public async getPermission(id: string) {
    return apiService.get<Permission>(`${this.baseUrl}/${id}`);
  }

  // Create permission
  public async createPermission(permissionData: CreatePermissionDto) {
    return apiService.post<Permission>(this.baseUrl, permissionData);
  }

  // Update permission
  public async updatePermission(id: string, permissionData: UpdatePermissionDto) {
    return apiService.patch<Permission>(`${this.baseUrl}/${id}`, permissionData);
  }

  // Delete permission
  public async deletePermission(id: string) {
    return apiService.delete<{ success: boolean }>(`${this.baseUrl}/${id}`);
  }

  // Check if user has permission
  public async checkPermission(userId: string, permissionCode: string) {
    return apiService.get<{ hasPermission: boolean }>(`/admin/users/${userId}/permissions/check/${permissionCode}`);
  }
}

export default new PermissionService(); 